// MA-586 — NARRATION PLAY LEDGER: the one test-observable record of what the
// narration player actually decided to play.
//
// WHY: e2e specs used to prove "the child hears X" by stubbing
// `speechSynthesis.speak` into `window.__speakCalls`. Once the pre-generated
// corpus covered the app, NarrationSourceComposite plays recorded audio for
// every manifest hit and never calls `speak()`, so those stubs recorded
// nothing and 12 spec files went red while the product worked. This ledger is
// written by the two lanes themselves at the moment each one starts:
//   - PregeneratedSource, right before `player.play()` on a manifest hit;
//   - WebSpeechSource, right before the first `synth.speak()` of a play().
// Both lanes are only ever reached through NarrationSourceComposite.play()
// (narrationSeamLint rules (a)/(c)), so this is the choke point's own record.
//
// ⭐`text` IS THE NARRATED TEXT, never merely the requested one. For the
// WebSpeech lane that is the string handed to the synthesizer. For the
// pre-generated lane it is the requested text ONLY when (a) the chosen
// manifest entry's `hashKey` equals the hash of that text under this
// surface's voice params AND (b) the object key the player will fetch
// (`<id>/<hash12>/audio.mp3` — the pipeline writes clips content-addressed)
// carries that same hash prefix (clipMatchesTextHash). (a) alone is nearly a
// tautology — the lookup map IS keyed by hashKey (MA-586 RED S-1) — so (b) is
// what catches a right key pointing at the wrong clip path. A clip failing
// either records `text: null`, so a spec asserting content fails.
// ⚠️What this proves is the PLAYER'S DECISION (which content-addressed clip it
// started), not that the bytes loaded or sound right: e2e uses a silent test
// player (RED S-4). Real-bytes coverage is the dispatch-only live lane
// .github/workflows/qa-drive-narration-spot-check.yml (reads the control's
// `data-narration-lane`, which reaches "clip" only when a real clip plays to
// its end) plus the MA-470 URL-wiring unit pins; no per-PR check loads bytes.
//
// OPT-IN, TEST-ONLY: nothing is recorded unless `window.__narrationPlays` is
// already an array. Only a Playwright init script ever creates it (the same
// opt-in-by-window-hook pattern as `__pwNarrationTestPlayerFactory` in
// useNarration.ts and `__PW_PIN_BUBBLE_DAY__`); for every real user it is
// undefined and `recordNarrationPlay` is a no-op read.

export interface NarrationPlayRecord {
  source: "pregenerated" | "webspeech";
  /** Manifest page id (e.g. `story:0003_vayu-the-wind:p0`) — pre-generated only. */
  audioId: string | null;
  /** Manifest-relative audio path — pre-generated only. */
  audioPath: string | null;
  /** The text this playback narrates (see header). `null` = a pre-generated
   *  page whose recording does NOT provably narrate the requested text. */
  text: string | null;
  /** What the caller asked to hear (diagnostics). */
  requestedText: string;
  /** The rate the caller requested (opts.rate), or null when omitted. */
  rate: number | null;
  /** opts.mode ("replay" | "decodeSupport"), or null when omitted. */
  mode: string | null;
}

type LedgerWindow = { __narrationPlays?: unknown };

function ledger(): NarrationPlayRecord[] | null {
  if (typeof window === "undefined") return null;
  const plays = (window as unknown as LedgerWindow).__narrationPlays;
  return Array.isArray(plays) ? (plays as NarrationPlayRecord[]) : null;
}

/** True only when a test has opted in (so callers can skip building a record). */
export function narrationPlayLedgerEnabled(): boolean {
  return ledger() !== null;
}

/** Append one record — a no-op unless a test opted in. Never throws: a
 *  broken ledger must never be able to silence the child's audio. */
export function recordNarrationPlay(record: NarrationPlayRecord): void {
  try {
    ledger()?.push(record);
  } catch {
    /* never let test instrumentation affect playback */
  }
}

/** MA-586 (RED S-1) — does this manifest entry's clip provably narrate the
 *  text whose hash is `requestHashKey`? True only when the entry's hashKey
 *  equals it AND the clip's content-addressed path segment
 *  (`<id>/<hash12>/audio.mp3`) is that hash's first 12 characters. */
export function clipMatchesTextHash(page: { hashKey: string; audioPath: string }, requestHashKey: string): boolean {
  if (page.hashKey !== requestHashKey) return false;
  const segments = page.audioPath.split("/");
  return segments.length >= 3 && segments[segments.length - 2] === requestHashKey.slice(0, 12);
}

/** Manifest page id from a manifest-relative audio path
 *  (`<id>/<hash12>/audio.mp3`, see narrationManifestClient.expandIndexEntry). */
export function audioIdFromPath(audioPath: string): string {
  const slash = audioPath.indexOf("/");
  return slash === -1 ? audioPath : audioPath.slice(0, slash);
}
